// product/ProductInfo.jsx
// Right-side panel: title, price, stock indicator, seller chip, description, buy button.
// Props:
//   product  — full product object from /api/marketplace/product/:storeSlug/:productSlug
//   onBuy    — callback to open PurchaseDrawer

import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Store, Clock, CheckCircle2, AlertTriangle, XCircle, ShoppingBag, ChevronDown, ChevronUp } from "lucide-react";
import { brand } from "../shared/brand";

// Characters before the description collapses
const DESCRIPTION_COLLAPSE_AT = 180;

export function ProductInfo({ product, onBuy }) {
  if (!product) return null;

  const {
    title, price, description,
    seller, stock_total, badge,
    in_fastmax,
  } = product;

  return (
    <div className="flex flex-col gap-5">

      {/* Seller chip */}
      {seller && (
        <Link
          to={`/${seller.store_slug || seller.username}`}
          className="inline-flex items-center gap-2 self-start rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50 transition-colors"
        >
          <Store className="size-3.5" style={{ color: brand.primary }} />
          {seller.username}
        </Link>
      )}

      {/* Title */}
      <h1 className="text-2xl md:text-3xl font-extrabold text-slate-900 leading-tight">
        {title}
      </h1>

      {/* Price row */}
      <div className="flex items-baseline gap-3">
        <span className="text-3xl font-extrabold" style={{ color: brand.primary }}>
          S/ {parseFloat(price).toFixed(2)}
        </span>
        {badge?.type === "hoy_provider" || badge?.type === "hoy_regular" ? (
          <span
            className="inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1 text-xs font-bold text-white"
            style={{ background: badge.type === "hoy_provider" ? brand.accentGreen : brand.accentGold }}
          >
            <Clock className="size-3" />
            {badge.label}
          </span>
        ) : null}
      </div>

      {/* Stock indicator */}
      <StockBadge total={stock_total} inFastmax={in_fastmax} />

      {/* Description */}
      {description && (
        <ExpandableDescription text={description} />
      )}

      {/* Buy button */}
      <button
        onClick={onBuy}
        disabled={stock_total <= 0}
        className="flex items-center justify-center gap-2 w-full rounded-2xl py-4 text-base font-extrabold text-white shadow-sm transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
        style={{ background: stock_total > 0 ? brand.primary : "#94a3b8" }}
      >
        <ShoppingBag className="size-5" />
        {stock_total > 0 ? "Comprar ahora" : "Sin stock"}
      </button>

      {/* Delivery note */}
      {stock_total > 0 && (
        <p className="text-xs text-slate-400 text-center -mt-2">
          Contraentrega disponible · Seguimiento en vivo incluido
        </p>
      )}
    </div>
  );
}

// ── Expandable description ────────────────────────────────────────────────────

function ExpandableDescription({ text }) {
  const [expanded, setExpanded] = useState(false);
  const isLong = text.length > DESCRIPTION_COLLAPSE_AT;
  const displayed = isLong && !expanded
    ? text.slice(0, DESCRIPTION_COLLAPSE_AT).trimEnd() + "…"
    : text;

  return (
    <div>
      <h2 className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-2">
        Descripción
      </h2>
      <p className="text-sm text-slate-600 leading-relaxed whitespace-pre-line">
        {displayed}
      </p>
      {isLong && (
        <button
          onClick={() => setExpanded(e => !e)}
          className="mt-2 inline-flex items-center gap-1 text-xs font-semibold transition-colors"
          style={{ color: brand.primary }}
        >
          {expanded ? (
            <><ChevronUp className="size-3.5" /> Ver menos</>
          ) : (
            <><ChevronDown className="size-3.5" /> Ver más</>
          )}
        </button>
      )}
    </div>
  );
}

// ── Stock badge ───────────────────────────────────────────────────────────────

function StockBadge({ total, inFastmax }) {
  if (total <= 0) {
    return (
      <div className="inline-flex items-center gap-1.5 text-sm font-semibold text-red-500">
        <XCircle className="size-4" />
        Sin stock
      </div>
    );
  }
  if (total <= 5) {
    return (
      <div className="inline-flex items-center gap-1.5 text-sm font-semibold text-amber-500">
        <AlertTriangle className="size-4" />
        Últimas {total} unidades
      </div>
    );
  }
  return (
    <div
      className="inline-flex items-center gap-1.5 text-sm font-semibold"
      style={{ color: brand.accentGreen }}
    >
      <CheckCircle2 className="size-4" />
      {inFastmax ? "En almacén Fast Max · Entrega hoy" : "Disponible"}
    </div>
  );
}